package example.shadcn_kotlin.ui.pages.docs.components

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.*
import com.varabyte.kobwebx.markdown.*
import example.shadcn_kotlin.ui.components.*
import example.shadcn_kotlin.ui.pages.components.demo.CommandDemo
import example.shadcn_kotlin.ui.pages.components.demo.CommandMenu

@Page
@Composable
fun CommandPage() {
    CompositionLocalProvider(LocalMarkdownContext provides MarkdownContext("docs/components/command.md", mapOf("title" to listOf("Command"), "description" to listOf("Fast, composable, unstyled command menu for React.")))) {
        example.shadcn_kotlin.ui.components.layouts.MDLayout {
            ComponentPreview("""import lucide_react.*
import lucide_react.Calendar
import react.FC
import react.Props
import react.ReactNode
import react.dom.html.ReactHTML.span
import shadcn.ui.components.*
import shadcn.ui.components.Command
import web.cssom.ClassName

val CommandDemo = FC<Props> {
    Command {
        className = ClassName("border rounded-lg shadow-md")
        CommandInput {
            placeholder = "Type a command or search..."
        }
        CommandList {
            CommandEmpty { +"No results found." }
            CommandGroup {
                heading = ReactNode("Suggestions")
                CommandItem {
                    Calendar { className = ClassName("w-4 h-4 mr-2") }
                    span { +"Calendar" }
                }
                CommandItem {
                    Smile { className = ClassName("w-4 h-4 mr-2") }
                    span { +"Search Emoji" }
                }
                CommandItem {
                    Calculator { className = ClassName("w-4 h-4 mr-2") }
                    span { +"Calculator" }
                }
            }
            CommandSeparator {}
            CommandGroup {
                heading = ReactNode("Settings")
                CommandItem {
                    User { className = ClassName("w-4 h-4 mr-2") }
                    span { +"Profile" }
                    CommandShortcut { +"⌘P" }
                }
                CommandItem {
                    CreditCard { className = ClassName("w-4 h-4 mr-2") }
                    span { +"Billing" }
                    CommandShortcut { +"⌘B" }
                }
            }
        }
    }
}"""){ 
 CommandDemo {} 
 }
            org.jetbrains.compose.web.dom.H2({ 
classes("font-heading","mt-12","scroll-m-20","border-b","pb-2","text-2xl","font-semibold","tracking-tight","first:mt-0")
id("about")
 }) {
                org.jetbrains.compose.web.dom.Text("About")
            }
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("The ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("<Command />")
                }
                org.jetbrains.compose.web.dom.Text(" component uses the ")
                org.jetbrains.compose.web.dom.A("https://cmdk.paco.me", { classes("font-medium","underline","underline-offset-4") }) {
                    org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                        org.jetbrains.compose.web.dom.Text("cmdk")
                    }
                }
                org.jetbrains.compose.web.dom.Text(" component by ")
                org.jetbrains.compose.web.dom.A("https://twitter.com/pacocoursey", { classes("font-medium","underline","underline-offset-4") }) {
                    org.jetbrains.compose.web.dom.Text("pacocoursey")
                }
                org.jetbrains.compose.web.dom.Text(".")
            }
            org.jetbrains.compose.web.dom.H2({ 
classes("font-heading","mt-12","scroll-m-20","border-b","pb-2","text-2xl","font-semibold","tracking-tight","first:mt-0")
id("usage")
 }) {
                org.jetbrains.compose.web.dom.Text("Usage")
            }
            CodeBlock("""import shadcn.ui.components.*
""", lang= "kotlin", highlight= "")
            CodeBlock("""Command {
    CommandInput { placeholder = "Type a command or search..." }
    CommandList {
        CommandEmpty { +"No results found." }
        CommandGroup {
            heading = ReactNode("Suggestions")
            CommandItem { +"Calendar" }
            CommandItem { +"Search Emoji" }
            CommandItem { +"Calculator" }
        }
        CommandSeparator {}
        CommandGroup {
            heading = ReactNode("Settings")
            CommandItem { +"Profile" }
            CommandItem { +"Billing" }
            CommandItem { +"Settings" }
        }
    }
}
""", lang= "kotlin", highlight= "")
            org.jetbrains.compose.web.dom.H2({ 
classes("font-heading","mt-12","scroll-m-20","border-b","pb-2","text-2xl","font-semibold","tracking-tight","first:mt-0")
id("examples")
 }) {
                org.jetbrains.compose.web.dom.Text("Examples")
            }
            org.jetbrains.compose.web.dom.H3({ 
classes("font-heading","mt-8","scroll-m-20","text-xl","font-semibold","tracking-tight")
id("dialog")
 }) {
                org.jetbrains.compose.web.dom.Text("Dialog")
            }
            ComponentPreview("""import react.dom.html.ReactHTML.p
import kotlinx.browser.document
import org.w3c.dom.events.Event
import react.*
import react.dom.html.ReactHTML.kbd
import react.dom.html.ReactHTML.span
import shadcn.ui.components.*
import web.cssom.ClassName

val CommandMenu = FC<Props> {
    val (open, setOpen) = useState(false)
    rawUseEffect({
        val down = { it: Event ->
            if (it.asDynamic().key === "k") {
                setOpen(!open)
            }
        }
        document.addEventListener("keydown", down)
        val cleanup: Cleanup =  { document.removeEventListener("keydown", down) }
        cleanup
    })

    p {
        className = ClassName("text-sm text-muted-foreground")
        + "Press "
        kbd {
            className = ClassName("pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100")
            span {
                className = ClassName("text-xs")
                + "⌘"
            }
            + "K"
        }
    }
    CommandDialog {
        this.open = open
        onOpenChange = setOpen

        CommandInput {
            placeholder = "Type a command or search..."
        }

        CommandList {
            CommandEmpty { +"No results found." }
            CommandGroup {
                CommandItem { +"Calendar" }
                CommandItem { +"Search Emoji " }
                CommandItem { +"Calculator" }
            }
        }
    }
}"""){ 
 CommandMenu {} 
 }
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("To show the command menu in a dialog, use the ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("CommandDialog {}")
                }
                org.jetbrains.compose.web.dom.Text(" component.")
            }
            CodeBlock("""val CommandMenu = FC<Props> {
    val (open, setOpen) = useState(false)
    rawUseEffect({
        val down = { it: Event ->
            if (it.asDynamic().key === "k") {
                setOpen(!open)
            }
        }
        document.addEventListener("keydown", down)
        val cleanup: Cleanup =  { document.removeEventListener("keydown", down) }
        cleanup
    })
    
    CommandDialog {
        this.open = open
        onOpenChange = setOpen
        
        CommandInput { placeholder = "Type a command or search..." }
        CommandList {
            CommandEmpty { +"No results found." }
            CommandGroup {
                CommandItem { +"Calendar" }
                CommandItem { +"Search Emoji " }
                CommandItem { +"Calculator" }
            }
        }
    }
}
""", lang= "kotlin", highlight= "")
            org.jetbrains.compose.web.dom.H3({ 
classes("font-heading","mt-8","scroll-m-20","text-xl","font-semibold","tracking-tight")
id("combobox")
 }) {
                org.jetbrains.compose.web.dom.Text("Combobox")
            }
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("You can use the ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("<Command />")
                }
                org.jetbrains.compose.web.dom.Text(" component as a combobox. See the ")
                org.jetbrains.compose.web.dom.A("/docs/components/combobox", { classes("font-medium","underline","underline-offset-4") }) {
                    org.jetbrains.compose.web.dom.Text("Combobox")
                }
                org.jetbrains.compose.web.dom.Text(" page for more information.")
            }
        }
    }
}
